import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, ClipboardList, Hash, Filter, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { API, pageVariants, cleanEmployeeName } from './utils';
import { useGuestPortal } from './GuestPortalContext';

export function SignInSheetView({ setView }) {
  const { t } = useGuestPortal();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const handleVerifyAccess = async () => {
    if (!accessCode) {
      toast.error("Please enter company name or employee ID");
      return;
    }
    setVerifying(true);
    if (accessCode.toLowerCase() === "cpkc") {
      setIsVerified(true);
      fetchRecords();
      toast.success("Access granted");
      setVerifying(false);
      return;
    }
    try {
      await axios.get(`${API}/guests/${accessCode}`);
      setIsVerified(true);
      fetchRecords();
      toast.success("Access granted");
    } catch (error) {
      toast.error("Invalid company name or employee ID");
    } finally {
      setVerifying(false);
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      // Guest-portal-friendly view: in-house guests + last 3 days of checkouts only.
      // The full /admin/records list is now 1000+ entries which (a) used to silently
      // truncate recent checkouts at the old 1000 cap and (b) is wasteful to ship to
      // a tablet. The guest portal almost never needs older history.
      const response = await axios.get(`${API}/admin/records?recent_only=true&recent_days=3`);
      setRecords(response.data);
      setLastRefreshed(new Date());
    } catch (error) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  if (!isVerified) {
    return (
      <motion.div
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Card className="glass-card p-8" data-testid="signin-access-card">
          <CardHeader className="pb-6">
            <button 
              onClick={() => setView("menu")} 
              className="text-vault-text-secondary hover:text-vault-gold transition-colors mb-4 flex items-center gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <CardTitle className="font-outfit text-2xl font-bold text-vault-text tracking-tight flex items-center gap-3">
              <ClipboardList className="w-6 h-6 text-vault-gold" />
              View Sign-In Sheet
            </CardTitle>
            <p className="text-vault-text-secondary font-manrope mt-2 text-sm">
              Enter your company name or employee ID to view records
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="vault-label">Company Name or Employee ID</label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" />
                <Input
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter company name or employee ID"
                  className="vault-input pl-10"
                  data-testid="access-code-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleVerifyAccess()}
                />
              </div>
            </div>
            <Button
              onClick={handleVerifyAccess}
              disabled={verifying}
              className="w-full vault-btn-primary h-12"
              data-testid="verify-access-btn"
            >
              {verifying ? "Verifying..." : "View Sign-In Sheet"}
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-6xl px-4"
    >
      <Card className="glass-card" data-testid="signin-sheet-view-card">
        <CardHeader className="pb-4">
          <button 
            onClick={() => setView("menu")} 
            className="text-vault-text-secondary hover:text-vault-gold transition-colors mb-4 flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Menu
          </button>
          <div className="text-center">
            <CardTitle className="font-outfit text-2xl font-bold text-vault-gold tracking-tight">
              Hodler Inn
            </CardTitle>
            <p className="text-vault-text-secondary text-sm mt-1">820 Hwy 59 N Heavener, OK, 74937</p>
            <p className="text-vault-text-secondary text-sm">Phone: 918-653-7801</p>
          </div>
          
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 bg-black/30 p-3 rounded-lg border border-vault-border">
            <Filter className="w-4 h-4 text-vault-gold" />
            <label className="text-vault-text-secondary text-sm cursor-pointer flex items-center gap-2">
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                className="w-4 h-4 accent-vault-gold cursor-pointer"
                data-testid="guest-show-active-only-toggle"
              />
              <span className={showActiveOnly ? "text-vault-gold font-medium" : ""}>In-House Only</span>
            </label>
            <div className="h-5 w-px bg-vault-border mx-1" />
            <Button
              onClick={fetchRecords}
              disabled={loading}
              size="sm"
              variant="ghost"
              className="h-8 px-3 text-vault-text-secondary hover:text-vault-gold hover:bg-vault-gold/10 flex items-center gap-2"
              data-testid="guest-refresh-records-btn"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            {lastRefreshed && (
              <span
                className="text-xs text-vault-text-secondary"
                data-testid="guest-last-refreshed"
                title={lastRefreshed.toLocaleString()}
              >
                Updated {lastRefreshed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
          
          <div className="mt-2 text-center text-sm text-vault-text-secondary">
            {(() => {
              // Backend already scopes the response to "in-house + last 3 days of checkouts"
              // (see fetchRecords). Local filter here just toggles whether to also include
              // recent checkouts in the display.
              const visible = showActiveOnly
                ? records.filter(r => !r.is_checked_out)
                : records;
              return showActiveOnly ? (
                <span>Showing <span className="text-vault-gold font-medium" data-testid="guest-inhouse-count">{visible.length}</span> in-house guests</span>
              ) : (
                <span>Showing <span className="text-vault-gold font-medium">{visible.length}</span> records (in-house + last 3 days)</span>
              );
            })()}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="text-center py-8 text-vault-text-secondary">Loading...</div>
          ) : (
            <div className="min-w-[1000px]">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-vault-border hover:bg-transparent">
                    <TableHead className="text-vault-gold w-12">#</TableHead>
                    <TableHead className="text-vault-gold">Stay Type</TableHead>
                    <TableHead className="text-vault-gold">Name</TableHead>
                    <TableHead className="text-vault-gold">Employee ID</TableHead>
                    <TableHead className="text-vault-gold">Signature In</TableHead>
                    <TableHead className="text-vault-gold">Signature Out</TableHead>
                    <TableHead className="text-vault-gold">Date In</TableHead>
                    <TableHead className="text-vault-gold">Time In</TableHead>
                    <TableHead className="text-vault-gold">Date Out</TableHead>
                    <TableHead className="text-vault-gold">Time Out</TableHead>
                    <TableHead className="text-vault-gold">Room #</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Backend already scoped to in-house + last 3 days of checkouts, so
                    // the only client-side filter is the In-House Only toggle.
                    const filteredRecords = showActiveOnly
                      ? records.filter(r => !r.is_checked_out)
                      : records;
                    
                    if (filteredRecords.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={11} className="text-center text-vault-text-secondary py-8">
                            {showActiveOnly ? "No in-house guests found" : "No records found"}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return filteredRecords.map((record, index) => (
                      <TableRow key={record.id} className="table-row border-vault-border">
                        <TableCell className="font-mono text-vault-text">{index + 1}</TableCell>
                        <TableCell className="text-vault-text">Single Stay</TableCell>
                        <TableCell className="text-vault-text font-medium">{cleanEmployeeName(record.employee_name)}</TableCell>
                        <TableCell className="font-mono text-vault-text">{record.employee_number}</TableCell>
                        <TableCell className="text-vault-success font-medium">{record.has_signature || record.signature ? "Signed" : "—"}</TableCell>
                        <TableCell className="text-vault-success font-medium">{record.is_checked_out && (record.has_signature || record.signature) ? "Signed" : "—"}</TableCell>
                        <TableCell className="text-vault-text">{record.check_in_date}</TableCell>
                        <TableCell className="font-mono text-vault-text">{record.check_in_time}</TableCell>
                        <TableCell className="text-vault-text">{record.check_out_date || "—"}</TableCell>
                        <TableCell className="font-mono text-vault-text">{record.check_out_time || "—"}</TableCell>
                        <TableCell className="font-mono text-vault-gold font-bold">{record.room_number}</TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-vault-text-secondary text-xs text-center py-4 md:hidden">
            ← Swipe table horizontally to see all columns →
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
